/* eslint-disable */

import React, { useState, useEffect, useCallback } from "react";
import { removeBackground } from "@imgly/background-removal";
import axios from "axios";
import Joyride, { STATUS } from "react-joyride";
import { MDBContainer, MDBBtn } from "mdb-react-ui-kit";
import { usePageState } from "../../context/AppStateContext";
import "./AddProductWizard.css";

import AutoFillLoader from "../Common/Loaders/AutoFillLoader";
import SubmissionLoader from "../Common/Loaders/SubmissionLoader";
import { useMessageBox } from "../Common/MessageBox";

import ImageUploadStep from "./ImageUploadStep";
import ListingDetailsStep from "./ListingDetailsStep";
import MarketplacesStep from "./MarketplacesStep";
import ImageProcessingStep from "./ImageProcessingStep";
import ReviewStep from "./ReviewStep";

import {
  steps,
  autoFillMessages,
  blurMessages,
  poshmarkSubCategoriesMap,
  marketplaces,
} from "./constants";
import { buildEbayAspects, buildMetafieldsFromAnalysis } from "./helpers";

const AddProduct = () => {
  const { state: persistedState, setState: setPersistedState } =
    usePageState("addProduct");
  const { showError, showSuccess, showWarning, MessageBoxComponent } =
    useMessageBox();

  const [currentStep, setCurrentStep] = useState(
    () => persistedState.currentStep || 1,
  );
  const [processingImages, setProcessingImages] = useState(false);
  const [progress, setProgress] = useState(0);
  const [blurTextIndex, setBlurTextIndex] = useState(0);
  const [stepIndex, setStepIndex] = useState(0);
  const [run, setRun] = useState(false);
  const [activeMetafields, setActiveMetafields] = useState([]);
  const [loadingMetafields, setLoadingMetafields] = useState(false);
  const companyId = localStorage.getItem("company_id");

  const [poshmarkConnected, setPoshmarkConnected] = useState(false);
  const [checkingPoshmark, setCheckingPoshmark] = useState(false);
  const [poshmarkSubCategories, setPoshmarkSubCategories] = useState([]);

  const [ebayConnected, setEbayConnected] = useState(false);
  const [checkingEbay, setCheckingEbay] = useState(false);

  const [isAutoFilling, setIsAutoFilling] = useState(false);
  const [autoFillMessageIndex, setAutoFillMessageIndex] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionStep, setSubmissionStep] = useState("");
  const [submissionSuccess, setSubmissionSuccess] = useState(false);
  const [autoFillResponse, setAutoFillResponse] = useState(null);

  const [savedEbayPolicies, setSavedEbayPolicies] = useState(null);

  const [formData, setFormData] = useState(() => ({
    uploadedImages: persistedState.formData?.uploadedImages || [],
    categoryAspects: persistedState.formData?.categoryAspects || [],
    allCategoryAspects: persistedState.formData?.allCategoryAspects || [],
    categoryId: persistedState.formData?.categoryId || "",
    blurStatuses: persistedState.formData?.blurStatuses || [],
    imageProcessing: false,
    backgroundRemoval: persistedState.formData?.backgroundRemoval || false,
    blurDetection: persistedState.formData?.blurDetection || false,
    upscaleImage: persistedState.formData?.upscaleImage || false,

    title: persistedState.formData?.title || "",
    description: persistedState.formData?.description || "",
    sku: persistedState.formData?.sku || "",
    price: persistedState.formData?.price || "",
    listingType: persistedState.formData?.listingType || "FIXED_PRICE",
    pricerange: persistedState.formData?.pricerange || "",
    quantity: persistedState.formData?.quantity || "1",
    brand: persistedState.formData?.brand || "",
    color: persistedState.formData?.color || "",
    category: persistedState.formData?.category || "",
    condition: persistedState.formData?.condition || "",
    condition_summary: persistedState.formData?.condition_summary || "",
    authenticity_score: persistedState.formData?.authenticity_score || "",
    authenticity_summary: persistedState.formData?.authenticity_summary || "",
    material: persistedState.formData?.material || "",
    tags: persistedState.formData?.tags || "",
    metafields: persistedState.formData?.metafields || {},

    variants: persistedState.formData?.variants || [
      { id: 1, size: "", color: "", sku: "" },
    ],

    selectedMarketplaces: persistedState.formData?.selectedMarketplaces || [],

    poshmarkCategory: persistedState.formData?.poshmarkCategory || "Women",
    poshmarkSubCategory: persistedState.formData?.poshmarkSubCategory || "",
    poshmarkSizeType: persistedState.formData?.poshmarkSizeType || "standard",
    poshmarkAvailability:
      persistedState.formData?.poshmarkAvailability || "available",

    vintedCategory: persistedState.formData?.vintedCategory || "",
    vintedCategoryId: persistedState.formData?.vintedCategoryId || "",
    vintedCategoryPath: persistedState.formData?.vintedCategoryPath || [],
    vintedPackageSize: persistedState.formData?.vintedPackageSize || "medium",
    vintedSize: persistedState.formData?.vintedSize || "",
  }));

  useEffect(() => {
    const { imageProcessing, ...persistableFormData } = formData;
    setPersistedState({
      formData: persistableFormData,
      currentStep,
    });
  }, [formData, currentStep, setPersistedState]);

  useEffect(() => {
    setRun(true);
  }, []);

  const stepstofollow = [
    {
      target: "#image-upload",
      content: "Upload your product images",
      disableBeacon: true,
      hideFooter: true,
      disableOverlayClose: true,
    },
  ];

  const fetchActiveMetafields = async () => {
    setLoadingMetafields(true);
    try {
      const response = await fetch(
        `https://crosslisting-backend-aacranbzdyd3ffbd.centralindia-01.azurewebsites.net/api/v1/getActiveShopifyMetafields/${companyId}`,
      );
      const result = await response.json();
      if (result.success) {
        console.log(result);
        setActiveMetafields(result.data.active_metafields);
      }
    } catch (err) {
      console.error("API fetch error:", err);
    } finally {
      setLoadingMetafields(false);
    }
  };

  const handleJoyrideCallback = (data) => {
    const { status } = data;
    if (status === STATUS.FINISHED) {
      setRun(false);
    }
  };

  const moveNext = () => {
    setStepIndex((prev) => prev + 1);
  };

  useEffect(() => {
    fetchActiveMetafields();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Check eBay store connection status
  useEffect(() => {
    const checkEbayStore = async () => {
      if (!formData.selectedMarketplaces.includes("ebay")) {
        setEbayConnected(false);
        return;
      }

      const companyId = localStorage.getItem("company_id");
      if (!companyId) {
        setEbayConnected(false);
        return;
      }

      try {
        setCheckingEbay(true);
        const res = await fetch(
          `https://crosslisting-backend-aacranbzdyd3ffbd.centralindia-01.azurewebsites.net/api/v1/getEbayStoreByCompany/${companyId}`,
        );
        const data = await res.json();

        if (res.ok && data.success && data.data) {
          setEbayConnected(true);
        } else {
          setEbayConnected(false);
        }
      } catch (err) {
        console.error("eBay store check error:", err);
        setEbayConnected(false);
      } finally {
        setCheckingEbay(false);
      }
    };

    checkEbayStore();
  }, [formData.selectedMarketplaces]);

  useEffect(() => {
    if (!formData.imageProcessing) return;
    const interval = setInterval(() => {
      setBlurTextIndex((prev) => (prev + 1) % blurMessages.length);
    }, 1500);
    return () => clearInterval(interval);
  }, [formData.imageProcessing]);

  useEffect(() => {
    if (!isAutoFilling) return;
    const interval = setInterval(() => {
      setAutoFillMessageIndex((prev) => (prev + 1) % autoFillMessages.length);
    }, 6000);
    return () => clearInterval(interval);
  }, [isAutoFilling, autoFillMessages.length]);

  useEffect(() => {
    const checkPoshmarkStatus = async () => {
      if (!formData.selectedMarketplaces.includes("poshmark")) {
        setPoshmarkConnected(false);
        return;
      }
      setCheckingPoshmark(true);
      try {
        const res = await fetch(
          "https://clextensions-a6bvf6amfrc9aeex.centralindia-01.azurewebsites.net/api/extension-status",
        );
        const data = await res.json();
        setPoshmarkConnected(data.connected);
        if (data.connected) {
          console.log("Poshmark extension connected");
        }
      } catch (err) {
        console.error("Poshmark status check failed:", err);
        setPoshmarkConnected(false);
      } finally {
        setCheckingPoshmark(false);
      }
    };
    checkPoshmarkStatus();
    const interval = setInterval(checkPoshmarkStatus, 30000);
    return () => clearInterval(interval);
  }, [formData.selectedMarketplaces]);

  const handlePoshmarkIntegrate = async (e) => {
    e.stopPropagation();
    try {
      setCheckingPoshmark(true);
      const statusRes = await fetch(
        "https://clextensions-a6bvf6amfrc9aeex.centralindia-01.azurewebsites.net/api/extension-status",
      );
      const statusData = await statusRes.json();
      if (!statusData.connected) {
        showWarning(
          "Please open Poshmark website in Chrome and refresh the page!",
        );
        window.open("https://poshmark.com/create-listing", "_blank");
      } else {
        showSuccess("Poshmark extension is connected and ready!");
        setPoshmarkConnected(true);
      }
    } catch (err) {
      console.error("Poshmark integration error:", err);
      showError("Failed to check Poshmark extension status");
    } finally {
      setCheckingPoshmark(false);
    }
  };

  const handlePoshmarkCategoryChange = (category) => {
    handleInputChange("poshmarkCategory", category);
    handleInputChange("poshmarkSubCategory", "");
    if (category && poshmarkSubCategoriesMap[category]) {
      setPoshmarkSubCategories(poshmarkSubCategoriesMap[category]);
    } else {
      setPoshmarkSubCategories([]);
    }
  };

  const uploadToPoshmark = async () => {
    setSubmissionStep("Uploading to Poshmark...");
    try {
      const healthCheck = await fetch(
        "https://clextensions-a6bvf6amfrc9aeex.centralindia-01.azurewebsites.net/api/extension-status",
      );
      const healthData = await healthCheck.json();
      if (!healthData.connected) {
        throw new Error(
          "Poshmark extension not connected. Please open Poshmark tab and refresh.",
        );
      }
      const files = formData.uploadedImages;
      const imagesBase64 = await Promise.all(
        files.map(async (url) => {
          const res = await fetch(url);
          const blob = await res.blob();
          return new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = (e) => resolve(e.target.result);
            reader.readAsDataURL(blob);
          });
        }),
      );
      if (imagesBase64.length === 0) {
        throw new Error("No valid images to upload");
      }
      const conditionMap = {
        new: "New With Tags",
        "like-new": "Like New",
        excellent: "Like New",
        good: "Like New",
        fair: "Like New",
        poor: "Like New",
      };
      const poshmarkPayload = {
        title: formData.title || "Product Listing",
        description: formData.description || "No description provided",
        price: parseFloat(formData.price || "1.00").toString(),
        category: formData.poshmarkCategory || "Women",
        subCategory: formData.poshmarkSubCategory || "Dresses",
        brand: formData.brand || "",
        sizeType: formData.poshmarkSizeType || "standard",
        size: formData.variants?.[0]?.size || "OS",
        condition: conditionMap[formData.condition] || "New With Tags",
        originalPrice: formData.pricerange?.split(" - ")[1] || "",
        color: formData.color || "",
        availability: formData.poshmarkAvailability || "available",
        images: imagesBase64,
      };
      console.log("Poshmark Payload:", poshmarkPayload);
      const poshmarkRes = await fetch(
        "https://clextensions-a6bvf6amfrc9aeex.centralindia-01.azurewebsites.net/api/start-automation",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(poshmarkPayload),
        },
      );
      const poshmarkData = await poshmarkRes.json();
      console.log("Poshmark Response:", poshmarkData);
      if (poshmarkRes.ok && poshmarkData.success) {
        return {
          success: true,
          data: poshmarkData,
          message: "Poshmark automation started! Check your browser.",
        };
      } else {
        throw new Error(poshmarkData.error || "Poshmark upload failed");
      }
    } catch (err) {
      console.error("Poshmark Error:", err);
      return {
        success: false,
        error: err.message,
      };
    }
  };

  const [processedImages, setProcessedImages] = useState([]);
  const [processing, setProcessing] = useState(false);

  const [vintedMainCategories, setVintedMainCategories] = useState([]);
  const [vintedSubCategories, setVintedSubCategories] = useState([]);
  const [vintedCategoryHistory, setVintedCategoryHistory] = useState([]);
  const [vintedCurrentCategoryId, setVintedCurrentCategoryId] = useState(null);
  const [vintedSelectedCategoryPath, setVintedSelectedCategoryPath] = useState(
    [],
  );

  // 🔗 Helper to ensure images are hosted on ERP (no blob: URLs in DB)
  const uploadImagesToERP = async (imageUrls) => {
    if (!Array.isArray(imageUrls) || imageUrls.length === 0) return [];

    // Separate already-hosted URLs and blob URLs
    const hostedUrls = [];
    const blobUrls = [];

    imageUrls.forEach((url) => {
      if (typeof url === "string" && url.startsWith("blob:")) {
        blobUrls.push(url);
      } else if (typeof url === "string" && url.startsWith("http")) {
        hostedUrls.push(url);
      }
    });

    // Nothing to upload, everything already hosted
    if (blobUrls.length === 0) {
      return imageUrls;
    }

    try {
      const formDataObj = new FormData();

      // Convert each blob URL to File and append as images[]
      for (let i = 0; i < blobUrls.length; i++) {
        const url = blobUrls[i];
        const res = await fetch(url);
        const blob = await res.blob();
        const file = new File([blob], `image-${Date.now()}-${i + 1}.png`, {
          type: blob.type || "image/png",
        });
        formDataObj.append("images[]", file);
      }

      const uploadRes = await fetch(
        "https://erp.thriftops.com/Upload/appImages.php",
        {
          method: "POST",
          body: formDataObj,
        },
      );

      let uploadedUrls = [];
      try {
        const data = await uploadRes.json();
        // Try to be flexible with response shape
        if (Array.isArray(data)) {
          uploadedUrls = data;
        } else if (Array.isArray(data.images)) {
          uploadedUrls = data.images;
        } else if (Array.isArray(data.data)) {
          uploadedUrls = data.data;
        }
      } catch (e) {
        console.error("ERP upload response is not JSON:", e);
      }

      if (!uploadedUrls || uploadedUrls.length === 0) {
        console.warn("ERP upload did not return image URLs, falling back.");
        return imageUrls;
      }

      // Merge already-hosted URLs with new ERP URLs
      return [...hostedUrls, ...uploadedUrls];
    } catch (err) {
      console.error("ERP image upload failed:", err);
      // On failure, keep original URLs (including blob) so other flows still work
      return imageUrls;
    }
  };

  // Helper function - isko useState ke baad add karo
  const getImagesToSubmit = () => {
    // Pehle check karo agar processedImages hain to wo do, nahi to uploadedImages do
    return processedImages && processedImages.length > 0
      ? processedImages
      : formData.uploadedImages;
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps

  const [vintedSelectedCategoryId, setVintedSelectedCategoryId] = useState("");

  // eslint-disable-next-line react-hooks/exhaustive-deps

  const [vintedSelectedCategoryName, setVintedSelectedCategoryName] =
    useState("");

  const [vintedConnected, setVintedConnected] = useState(false);
  const [checkingVinted, setCheckingVinted] = useState(false);
  const [vintedLoadingCategories, setVintedLoadingCategories] = useState(false);

  const fetchVintedMainCategories = async () => {
    setVintedLoadingCategories(true);
    try {
      const res = await fetch(
        "https://clextensions-a6bvf6amfrc9aeex.centralindia-01.azurewebsites.net/api/categories/main",
      );
      const data = await res.json();
      if (data.success) {
        setVintedMainCategories(data.categories);
      } else {
        showError("Failed to load Vinted categories");
      }
    } catch (err) {
      console.error("Vinted categories fetch error:", err);
      showError("Could not connect to Vinted categories server");
    } finally {
      setVintedLoadingCategories(false);
    }
  };

  const fetchVintedSubCategories = async (parentCategoryId) => {
    setVintedLoadingCategories(true);
    try {
      const res = await fetch(
        `https://clextensions-a6bvf6amfrc9aeex.centralindia-01.azurewebsites.net/api/categories/sub/${parentCategoryId}`,
      );
      const data = await res.json();
      if (data.success) {
        setVintedSubCategories(data.subcategories);
        return data.subcategories;
      } else {
        showError("Failed to load Vinted subcategories");
        return [];
      }
    } catch (err) {
      console.error("Vinted subcategories fetch error:", err);
      showError("Could not connect to Vinted categories server");
      return [];
    } finally {
      setVintedLoadingCategories(false);
    }
  };

  const fetchVintedCategoryPath = async (categoryId) => {
    try {
      const res = await fetch(
        `https://clextensions-a6bvf6amfrc9aeex.centralindia-01.azurewebsites.net/api/categories/path/${categoryId}`,
      );
      const data = await res.json();
      if (data.success) {
        return data.path || [];
      } else {
        return [];
      }
    } catch (err) {
      console.error("Vinted category path error:", err);
      return [];
    }
  };

  const handleVintedCategoryClick = async (category) => {
    if (category.is_final_selection) {
      const path = await fetchVintedCategoryPath(category.category_id);
      const pathNames = path.map((cat) => cat.name);
      setFormData((prev) => ({
        ...prev,
        vintedCategory: pathNames.join(" > "),
        vintedCategoryId: category.category_id,
        vintedCategoryPath: pathNames,
      }));
      setVintedSelectedCategoryPath(pathNames);
      setVintedSelectedCategoryId(category.category_id);
      setVintedSelectedCategoryName(category.name);
      setVintedCurrentCategoryId(null);
      setVintedCategoryHistory([]);
    } else {
      setVintedCategoryHistory((prev) => [
        ...prev,
        {
          categoryId: vintedCurrentCategoryId,
          categoryName:
            vintedSelectedCategoryPath[vintedSelectedCategoryPath.length - 1] ||
            "",
        },
      ]);
      setVintedSelectedCategoryPath((prev) => [...prev, category.name]);
      setVintedCurrentCategoryId(category.category_id);
      const subs = await fetchVintedSubCategories(category.category_id);
      setVintedSubCategories(subs);
    }
  };

  const handleVintedBack = async () => {
    if (vintedCategoryHistory.length > 0) {
      const newPath = [...vintedSelectedCategoryPath];
      newPath.pop();
      setVintedSelectedCategoryPath(newPath);
      const prevCategory =
        vintedCategoryHistory[vintedCategoryHistory.length - 1];
      setVintedCategoryHistory((prev) => prev.slice(0, -1));
      setVintedCurrentCategoryId(prevCategory.categoryId);
      if (prevCategory.categoryId) {
        await fetchVintedSubCategories(prevCategory.categoryId);
      } else {
        setVintedCurrentCategoryId(null);
      }
    } else {
      setVintedCurrentCategoryId(null);
      setVintedSelectedCategoryPath([]);
    }
  };

  const resetVintedCategory = () => {
    setFormData((prev) => ({
      ...prev,
      vintedCategory: "",
      vintedCategoryId: "",
      vintedCategoryPath: [],
    }));
    setVintedSelectedCategoryPath([]);
    setVintedSelectedCategoryId("");
    setVintedSelectedCategoryName("");
    setVintedCurrentCategoryId(null);
    setVintedCategoryHistory([]);
    fetchVintedMainCategories();
  };

  const handleVintedIntegrate = async (e) => {
    e.stopPropagation();
    try {
      setCheckingVinted(true);

      // Pehle check karo extension connected hai ya nahi
      const res = await fetch(
        "https://clextensions-a6bvf6amfrc9aeex.centralindia-01.azurewebsites.net/api/health-vinted",
      );
      const data = await res.json();

      if (data.connected) {
        // ✅ Already connected - Directly Vinted new listing page kholo
        showSuccess("Opening Vinted listing page...");
        window.open("https://www.vinted.com/items/new", "_blank");
        setVintedConnected(true);
      } else {
        // ❌ Not connected - User ko guide karo
        showWarning(
          "Please open Vinted website in Chrome and refresh the page!",
        );
        window.open("https://www.vinted.com/items/new", "_blank");
        setVintedConnected(false);
      }
    } catch (err) {
      console.error("Vinted integration error:", err);
      showError("Failed to check Vinted extension status");
      setVintedConnected(false);
    } finally {
      setCheckingVinted(false);
    }
  };

  const uploadToVinted = async () => {
    setSubmissionStep("🚀 Starting Vinted automation...");

    // ===== STEP 1: GET USER ID FROM LOCALSTORAGE =====
    const companyId = localStorage.getItem("company_id");
    const effectiveUserId = companyId || "default-user";

    console.log(
      "📤 Vinted Upload - User ID from localStorage:",
      effectiveUserId,
    );

    // ===== STEP 2: SYNC USER ID WITH BACKEND VIA API =====
    if (
      companyId &&
      companyId !== "default-user" &&
      companyId !== "null" &&
      companyId !== "undefined"
    ) {
      try {
        console.log("📤 Syncing userId with backend API:", companyId);

        const syncResponse = await fetch(
          "https://clextensions-a6bvf6amfrc9aeex.centralindia-01.azurewebsites.net/api/set-user-id",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            body: JSON.stringify({
              userId: companyId,
              source: "vinted-upload",
              timestamp: Date.now(),
            }),
          },
        );

        if (!syncResponse.ok) {
          console.warn("⚠️ Sync response not OK:", syncResponse.status);
        } else {
          const syncData = await syncResponse.json();
          console.log("✅ Backend sync response:", syncData);

          if (syncData.success) {
            console.log(
              `✅ UserId ${companyId} synced successfully! Broadcasted to ${syncData.clients || 0} clients`,
            );
          }
        }

        // Small delay for WebSocket to propagate
        console.log("⏳ Waiting 2 seconds for WebSocket to propagate...");
        await new Promise((resolve) => setTimeout(resolve, 2000));
      } catch (syncError) {
        console.error("❌ Failed to sync userId with backend:", syncError);
        // Continue anyway - might still work with default ID
        console.log("⚠️ Continuing with userId:", effectiveUserId);
      }
    } else {
      console.log("⚠️ No valid companyId found, using:", effectiveUserId);
    }
    // ===== END USER ID SYNC =====

    try {
      // ===== STEP 3: CHECK EXTENSION HEALTH WITH USER ID =====
      setSubmissionStep("📡 Checking Vinted extension connection...");

      const healthUrl = `https://clextensions-a6bvf6amfrc9aeex.centralindia-01.azurewebsites.net/api/health-vinted?userId=${effectiveUserId}`;
      console.log("🔍 Health check URL:", healthUrl);

      const healthCheck = await fetch(healthUrl);

      if (!healthCheck.ok) {
        console.warn("⚠️ Health check response not OK:", healthCheck.status);
        const errorText = await healthCheck.text();
        console.warn("Error response:", errorText);
        throw new Error(`Health check failed: ${healthCheck.status}`);
      }

      const healthData = await healthCheck.json();
      console.log("📊 Health check response:", healthData);

      if (!healthData.connected) {
        // Check if there are any active connections but with different user ID
        if (healthData.activeConnections && healthData.activeConnections > 0) {
          showError(
            `❌ Extension Connected But Wrong User ID!\n\n` +
              `Your User ID: ${effectiveUserId}\n` +
              `Active Connections: ${healthData.activeConnections}\n\n` +
              `The extension is connected but with a different user ID.\n` +
              `Please wait 5 seconds and try again.`,
            10000,
          );

          // Try to force sync again
          if (companyId) {
            console.log("🔄 Retrying userId sync...");
            await fetch(
              "https://clextensions-a6bvf6amfrc9aeex.centralindia-01.azurewebsites.net/api/set-user-id",
              {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ userId: companyId }),
              },
            );
          }
        } else {
          // No connections at all
          showError(
            "❌ Vinted extension not connected!\n\n" +
              "Current Status:\n" +
              `• User ID: ${effectiveUserId}\n` +
              `• Active Connections: ${healthData.activeConnections || 0}\n\n` +
              "Please follow these steps:\n" +
              "1. Open Vinted website in Chrome\n" +
              "2. Go to create listing page (https://www.vinted.com/items/new)\n" +
              "3. Refresh the page\n" +
              "4. Wait for 'Ready for Automation' notification (2-3 seconds)\n" +
              "5. Click 'Create Listing' again\n\n" +
              "The notification will appear in the top-right corner of Vinted tab.",
          );

          window.open("https://www.vinted.com/items/new", "_blank");
        }

        return {
          success: false,
          error: "Extension not connected",
          requiresUserAction: true,
          healthData: healthData,
        };
      }

      console.log(
        "✅ Extension connected successfully for user:",
        effectiveUserId,
      );
      // ===== END HEALTH CHECK =====

      // ===== STEP 4: PROCESS IMAGES =====
      setSubmissionStep("📸 Processing images...");
      const imagesToProcess = getImagesToSubmit();

      if (!imagesToProcess || imagesToProcess.length === 0) {
        throw new Error("No images to upload");
      }

      console.log(`🖼️ Processing ${imagesToProcess.length} images...`);

      const imagesBase64 = await Promise.all(
        imagesToProcess.map(async (url, index) => {
          try {
            console.log(
              `📸 Processing image ${index + 1}/${imagesToProcess.length}`,
            );
            const res = await fetch(url);
            const blob = await res.blob();
            return new Promise((resolve) => {
              const reader = new FileReader();
              reader.onloadend = () => resolve(reader.result);
              reader.readAsDataURL(blob);
            });
          } catch (imgError) {
            console.error(
              `❌ Error processing image ${index + 1}:`,
              url,
              imgError,
            );
            return null;
          }
        }),
      );

      const validImages = imagesBase64.filter((img) => img !== null);

      if (validImages.length === 0) {
        throw new Error("No valid images to upload after processing");
      }

      console.log(`✅ Processed ${validImages.length} images successfully`);
      // ===== END IMAGE PROCESSING =====

      // ===== STEP 5: PREPARE PAYLOAD =====
      setSubmissionStep("📦 Preparing Vinted data...");

      // Map condition to Vinted format
      const conditionMap = {
        new: "New with tags",
        "like-new": "Very good",
        excellent: "Very good",
        good: "Good",
        fair: "Satisfactory",
        poor: "Satisfactory",
      };

      // Map package size
      const packageSizeMap = {
        small: "small",
        medium: "medium",
        large: "large",
        extra_large: "large",
      };

      const payload = {
        userId: effectiveUserId,
        title: formData.title || "Product Listing",
        description: formData.description || "No description provided",
        price: parseFloat(formData.price || "1.00"),
        category: formData.vintedCategory || "",
        brand: formData.brand || "",
        condition: conditionMap[formData.condition] || "Very good",
        size: formData.vintedSize || "M / US 8-10",
        packageSize: packageSizeMap[formData.vintedPackageSize] || "medium",
        color: formData.color || "",
        images: validImages,
      };

      console.log("📤 Vinted Payload:", {
        ...payload,
        images: `${payload.images.length} images (base64 data hidden)`,
      });

      // Validate required fields
      if (!payload.title) throw new Error("Title is required");
      if (!payload.description) throw new Error("Description is required");
      if (!payload.price || isNaN(payload.price))
        throw new Error("Valid price is required");
      if (!payload.condition) throw new Error("Condition is required");
      // ===== END PAYLOAD PREP =====

      // ===== STEP 6: SEND TO VINTED API =====
      setSubmissionStep(
        "⏳ Uploading to Vinted (this may take up to 2 minutes)...",
      );

      const apiUrl = `https://clextensions-a6bvf6amfrc9aeex.centralindia-01.azurewebsites.net/api/start-automation-vinted`;
      console.log("📡 API URL:", apiUrl);

      // Set timeout for fetch
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 150000); // 2.5 minutes timeout

      const res = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(payload),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      console.log("📨 Response status:", res.status);

      if (!res.ok) {
        const errorText = await res.text();
        console.error("❌ Error response:", errorText);
        throw new Error(`Server responded with ${res.status}: ${errorText}`);
      }

      const data = await res.json();
      console.log("📩 Vinted Response:", data);
      // ===== END API CALL =====

      // ===== STEP 7: HANDLE RESPONSE =====
      if (data.success) {
        const productId = data.product?.id;
        const productUrl = data.product?.url;

        console.log(`🎉 VINTED SUCCESS! Product ID: ${productId}`);
        console.log(`🔗 Product URL: ${productUrl}`);

        // Show success message
        showSuccess(
          `✅ Vinted Upload Successful!\n\n` +
            `📦 Product ID: ${productId}\n` +
            `🔗 URL: ${productUrl}\n\n` +
            `📋 The item has been uploaded and saved to database.`,
          8000,
        );

        // Try to focus Vinted tab
        try {
          if (typeof chrome !== "undefined" && chrome.tabs) {
            chrome.tabs.query({ url: "*://*.vinted.com/*" }, (tabs) => {
              if (tabs && tabs.length > 0) {
                chrome.tabs.update(tabs[0].id, { active: true });
              }
            });
          }
        } catch (e) {
          console.log("Could not focus Vinted tab:", e);
        }

        return {
          success: true,
          data: data,
          productId: productId,
          productUrl: productUrl,
          message: "Vinted upload complete! Product saved to database.",
          requiresUserAction: false,
        };
      } else if (data.timeout) {
        // Timeout case
        console.warn("⚠️ Vinted timeout:", data.error);

        showWarning(
          "⚠️ Vinted Upload Timeout\n\n" +
            "The upload may have completed but we couldn't get the product ID.\n\n" +
            "Please check your Vinted account to verify.",
          10000,
        );

        return {
          success: false,
          error: data.error,
          timeout: true,
          requiresUserAction: false,
        };
      } else {
        // Failure case
        throw new Error(data.error || "Vinted upload failed");
      }
      // ===== END RESPONSE HANDLING =====
    } catch (err) {
      console.error("❌ Vinted Error:", err);

      // Check for specific error types
      if (err.name === "AbortError") {
        showWarning(
          "⚠️ Upload timeout - Request took too long\n\n" +
            "Please check your Vinted account manually.",
        );

        return {
          success: false,
          error: "Request timeout",
          timeout: true,
          requiresUserAction: false,
        };
      }

      if (err.message.includes("Extension not connected")) {
        showError(
          "❌ Vinted extension not connected!\n\n" +
            "Please open Vinted and refresh the page.",
        );

        return {
          success: false,
          error: err.message,
          requiresUserAction: true,
        };
      } else if (
        err.message.includes("timeout") ||
        err.message.includes("TIMEOUT")
      ) {
        showWarning("⚠️ Upload timeout - Please check Vinted manually");

        return {
          success: false,
          error: err.message,
          timeout: true,
          requiresUserAction: false,
        };
      } else {
        showError(`❌ Vinted Error: ${err.message}`);

        return {
          success: false,
          error: err.message,
          requiresUserAction: false,
        };
      }
    }
  };

  const handleFileUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (!files.length) return;
    const validTypes = ["image/jpeg", "image/png", "image/webp"];
    const maxSize = 5 * 1024 * 1024;
    const validFiles = files.filter((file) => {
      if (!validTypes.includes(file.type)) {
        showWarning(`${file.name} is not a valid image.`);
        return false;
      }
      if (file.size > maxSize) {
        showWarning(`${file.name} exceeds 5MB.`);
        return false;
      }
      return true;
    });
    if (!validFiles.length) return;
    setFormData((prev) => ({
      ...prev,
      imageProcessing: true,
      title: "",
      description: "",
      brand: "",
      category: "",
      material: "",
      color: "",
      price: "",
      pricerange: "",
      condition: "",
      condition_summary: "",
      authenticity_score: "",
      authenticity_summary: "",
      tags: "",
      categoryAspects: [],
      allCategoryAspects: [],
      categoryId: "",
    }));
    // try {
    //   const formDataObj = new FormData();
    //   validFiles.forEach((file) => formDataObj.append("images", file));
    //   const blurRes = await fetch(
    //     "https://crosslisting-backend-aacranbzdyd3ffbd.centralindia-01.azurewebsites.net/api/blur/blur-detect",
    //     {
    //       method: "POST",
    //       body: formDataObj,
    //     },
    //   );
    //   // if (!blurRes.ok) throw new Error("Blur detection failed");
    //   const blurResults = await blurRes.json();
    //   if (!Array.isArray(blurResults)) {
    //     throw new Error("Invalid blur API response");
    //   }
    //   const acceptedImages = [];
    //   const blurStatuses = [];
    //   blurResults.forEach((item) => {
    //     const file = validFiles.find((f) => f.name === item.filename);
    //     if (!file) return;
    //     const isRejected = item.result.status === "rejected";
    //     blurStatuses.push(isRejected);
    //     acceptedImages.push(URL.createObjectURL(file));
    //   });
    //   setFormData((prev) => ({
    //     ...prev,
    //     uploadedImages: [...prev.uploadedImages, ...acceptedImages],
    //     blurStatuses,
    //   }));
    // } catch (err) {
    //   console.error("Blur detection error:", err);
    //   showError("Failed to process images");
    // } finally {
    //   setFormData((prev) => ({ ...prev, imageProcessing: false }));
    // }
    try {
      const acceptedImages = validFiles.map((file) =>
        URL.createObjectURL(file),
      );
      const blurStatuses = validFiles.map(() => false); // mark all as not rejected

      setFormData((prev) => ({
        ...prev,
        uploadedImages: [...prev.uploadedImages, ...acceptedImages],
        blurStatuses,
      }));
    } catch (err) {
      console.error("Image processing error:", err);
      showError("Failed to process images");
    } finally {
      setFormData((prev) => ({ ...prev, imageProcessing: false }));
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const fakeEvent = { target: { files: files } };
      handleFileUpload(fakeEvent);
    }
  };

  const handleStartImageEditing = async () => {
    if (!formData.uploadedImages.length) return;

    setProcessing(true);
    setProcessedImages([]);

    try {
      const processSingleImage = async (imageUrl, index) => {
        const res = await fetch(imageUrl);
        const blob = await res.blob();
        let file = new File([blob], `image-${index + 1}.png`, {
          type: blob.type,
        });

        return { file, blob };
      };

      // Load all images first
      const loadedImages = await Promise.all(
        formData.uploadedImages.map((img, i) => processSingleImage(img, i)),
      );

      const finalImages = [];

      // ================== 1️⃣ UPSCALE FIRST IMAGE ==================
      if (formData.upscaleImage && loadedImages[0]) {
        try {
          const form = new FormData();
          form.append("image", loadedImages[0].file);

          const response = await axios.post(
            "https://crosslisting-backend-aacranbzdyd3ffbd.centralindia-01.azurewebsites.net/api/image/ghost-mannequin",
            form,
          );

          if (response.data?.image) {
            const scaledBlob = await (await fetch(response.data.image)).blob();
            const scaledFile = new File([scaledBlob], `upscale-1.png`, {
              type: scaledBlob.type,
            });

            finalImages.push(URL.createObjectURL(scaledFile)); // ✅ ADD AS EXTRA IMAGE
          }
        } catch (err) {
          console.warn("Upscale failed", err);
        }
      }

      // ================== 2️⃣ BACKGROUND REMOVE ALL ==================
      const bgTasks = loadedImages.map(async ({ file }, index) => {
        if (!formData.backgroundRemoval) return URL.createObjectURL(file);

        try {
          const form = new FormData();
          form.append("image", file);

          const bgResponse = await axios.post(
            "https://crosslisting-backend-aacranbzdyd3ffbd.centralindia-01.azurewebsites.net/api/image/remove-background",
            form,
          );

          if (bgResponse.data?.success && bgResponse.data?.image) {
            const base64ToBlob = (base64) => {
              const arr = base64.split(",");
              const mime = arr[0].match(/:(.*?);/)[1];
              const bstr = atob(arr[1]);
              let n = bstr.length;
              const u8arr = new Uint8Array(n);
              while (n--) u8arr[n] = bstr.charCodeAt(n);
              return new Blob([u8arr], { type: mime });
            };

            const processedBlob = base64ToBlob(bgResponse.data.image);
            const bgFile = new File([processedBlob], `bg-${index + 1}.png`, {
              type: processedBlob.type,
            });

            return URL.createObjectURL(bgFile);
          }
        } catch (err) {
          console.warn("BG remove failed", err);
        }

        return URL.createObjectURL(file); // fallback original
      });

      const bgImages = await Promise.all(bgTasks);

      // Add BG images after upscale
      finalImages.push(...bgImages);

      setProcessedImages(finalImages);
      showSuccess("Image processing completed!");
    } catch (err) {
      console.error("Image processing error:", err);
      showError("Image processing failed");
    } finally {
      setProcessing(false);
    }
  };

  const handleInputChange = (field, value) => {
    if (field.startsWith("metafields.")) {
      const key = field.split(".")[1];
      setFormData((prev) => ({
        ...prev,
        metafields: {
          ...prev.metafields,
          [key]: value,
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  const handleVariantChange = (variantId, field, value) => {
    setFormData((prev) => ({
      ...prev,
      variants: prev.variants.map((variant) =>
        variant.id === variantId ? { ...variant, [field]: value } : variant,
      ),
    }));
  };

  const addVariant = () => {
    const newId = Math.max(...formData.variants.map((v) => v.id), 0) + 1;
    setFormData((prev) => ({
      ...prev,
      variants: [...prev.variants, { id: newId, size: "", color: "", sku: "" }],
    }));
  };

  const removeVariant = (variantId) => {
    if (formData.variants.length > 1) {
      setFormData((prev) => ({
        ...prev,
        variants: prev.variants.filter((variant) => variant.id !== variantId),
      }));
    }
  };
  const getEbayPolicies = async () => {
    const company_id = localStorage.getItem("company_id");
    console.log("company_id", company_id);
    if (!company_id) return; // ❌ no company id → exit

    try {
      // 1️⃣ get ebay policies
      // const res = await fetch(
      //   `https://crosslisting-backend-aacranbzdyd3ffbd.centralindia-01.azurewebsites.net/api/v1/getEbayPolicies/${company_id}`,
      // );
      // const policies = await res.json();

      // 2️⃣ get saved ebay policies
      const savedRes = await fetch(
        `https://crosslisting-backend-aacranbzdyd3ffbd.centralindia-01.azurewebsites.net/api/v1/getSavedEbayPolicies/${company_id}`,
      );
      const savedPolicies = await savedRes.json();

      setSavedEbayPolicies(savedPolicies);
      // 3️⃣ just store responses (no setSelected)
      //setEbayPolicies(policies);
      setSavedEbayPolicies(savedPolicies);
    } catch (error) {
      console.error("eBay policy fetch failed:", error);
    }
  };

  const handleMarketplaceToggle = (marketplaceId) => {
    setFormData((prev) => {
      const updatedMarketplaces = prev.selectedMarketplaces.includes(
        marketplaceId,
      )
        ? prev.selectedMarketplaces.filter((id) => id !== marketplaceId)
        : [...prev.selectedMarketplaces, marketplaceId];

      console.log("selected marketplaces", updatedMarketplaces);

      return {
        ...prev,
        selectedMarketplaces: updatedMarketplaces,
      };
    });

    if (marketplaceId === "vinted") {
      fetchVintedMainCategories();
    }

    if (marketplaceId === "ebay") {
      getEbayPolicies();
    }
  };

  // Check SmartScan/demo limits for company 13 before proceeding
  const checkUsageLimit = async () => {
    try {
      const company_id = localStorage.getItem("company_id");
      // Only enforce limit for demo company 13
      if (company_id !== "13") return true;

      const res = await fetch(
        `https://crosslisting-backend-aacranbzdyd3ffbd.centralindia-01.azurewebsites.net/api/smartscan//counts?company_id=${company_id}`,
      );
      const data = await res.json();

      if (!res.ok || !data?.success) {
        console.warn(
          "Smartscan counts API failed or returned invalid data:",
          data,
        );
        return true; // Don't block flow on API failure
      }

      const smartscanCount = Number(data.data?.smartscan || 0);
      const productsCount = Number(data.data?.products || 0);

      if (smartscanCount >= 100 || productsCount >= 100) {
        showWarning(
          "Your SmartScan demo limit has been reached. You have created 100 items. Please contact ThriftOps support to upgrade your plan or extend your usage limit.",
        );
        return false;
      }

      return true;
    } catch (err) {
      console.error("Error checking SmartScan usage limits:", err);
      // Fail-open: allow user to proceed if limit check fails
      return true;
    }
  };

  const nextStep = async () => {
    if (currentStep < steps.length) {
      // When moving from step 1 (image upload), enforce demo limit for company 13
      if (currentStep === 1) {
        const canProceed = await checkUsageLimit();
        if (!canProceed) return;
      }

      setCurrentStep((prev) => prev + 1);
      if (currentStep === 6) {
        runImageProcessing();
      }
    }
  };

  const runImageProcessing = async () => {
    setProcessingImages(true);
    setProgress(0);
    const finalImages = [];
    for (let i = 0; i < formData.uploadedImages.length; i++) {
      await new Promise((resolve) => setTimeout(resolve, 50));
      try {
        let res = await fetch(formData.uploadedImages[i]);
        let blob = await res.blob();
        let file = new File([blob], `image-${i + 1}.png`, { type: blob.type });
        if (formData.upscaleImage) {
          const form = new FormData();
          form.append("images", file);
          const response = await axios.post(
            "https://crosslisting-backend-aacranbzdyd3ffbd.centralindia-01.azurewebsites.net/api/scaleup/scale-up",
            form,
          );
          const scaledUrl = response.data?.[0]?.result?.url;
          const scaledBlob = await (await fetch(scaledUrl)).blob();
          file = new File([scaledBlob], `scaled-${i + 1}.png`, {
            type: scaledBlob.type,
          });
        }
        if (formData.backgroundRemoval) {
          const bgBlob = await removeBackground(file);
          file = new File([bgBlob], `bg-${i + 1}.png`, { type: bgBlob.type });
        }
        finalImages.push(URL.createObjectURL(file));
        setProcessedImages([...finalImages]);
        setProgress(
          Math.round(((i + 1) / formData.uploadedImages.length) * 100),
        );
      } catch (err) {
        console.error("Image processing failed for image", i, err);
      }
    }
    setProcessingImages(false);
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  const goToStep = (stepId) => {
    if (stepId <= getMaxAccessibleStep()) {
      setCurrentStep(stepId);
    }
  };

  const getMaxAccessibleStep = () => {
    let maxStep = 1;
    if (formData.uploadedImages.length > 0) maxStep = 2;
    if (
      formData.uploadedImages.length > 0 &&
      formData.title &&
      formData.description &&
      formData.sku &&
      formData.price &&
      formData.quantity
    ) {
      maxStep = 3;
    }
    if (
      formData.uploadedImages.length > 0 &&
      formData.title &&
      formData.description &&
      formData.sku &&
      formData.price &&
      formData.quantity &&
      formData.selectedMarketplaces.length > 0
    ) {
      maxStep = 4;
    }
    if (
      formData.uploadedImages.length &&
      formData.title &&
      formData.description &&
      formData.sku &&
      formData.price &&
      formData.quantity &&
      formData.selectedMarketplaces.length > 0
    ) {
      maxStep = 5;
    }
    return maxStep;
  };

  const isStepClickable = (stepId) => {
    return stepId <= getMaxAccessibleStep();
  };

  const isStepValid = () => {
    switch (currentStep) {
      case 1:
        return formData.uploadedImages.length > 0;
      case 2:
        return (
          formData.title &&
          formData.description &&
          formData.sku &&
          formData.price &&
          formData.quantity
        );
      case 3:
        return formData.selectedMarketplaces.length > 0;
      case 4:
        return true;
      case 5:
        return true;
      default:
        return false;
    }
  };

  const removeImage = (index) => {
    setFormData((prev) => ({
      ...prev,
      uploadedImages: prev.uploadedImages.filter((_, i) => i !== index),
      blurStatuses: prev.blurStatuses.filter((_, i) => i !== index),
    }));
  };

// Right Rotate (Clockwise)
const rotateImage = async (index) => {
  await rotateImageByDegrees(index, 90);
};

// Left Rotate (Counter-Clockwise)
const rotateImageLeft = async (index) => {
  await rotateImageByDegrees(index, -90);
};

// Common rotate function - WITHOUT SUCCESS MESSAGE
const rotateImageByDegrees = async (index, degrees) => {
  const imageUrl = formData.uploadedImages[index];
  
  try {
    const img = new Image();
    img.crossOrigin = "Anonymous";
    
    const loadedImage = await new Promise((resolve, reject) => {
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = imageUrl;
    });
    
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    const radians = degrees * Math.PI / 180;
    const isVertical = Math.abs(degrees % 180) === 90;
    
    if (isVertical) {
      canvas.width = loadedImage.height;
      canvas.height = loadedImage.width;
    } else {
      canvas.width = loadedImage.width;
      canvas.height = loadedImage.height;
    }
    
    ctx.translate(canvas.width / 2, canvas.height / 2);
    ctx.rotate(radians);
    ctx.drawImage(loadedImage, -loadedImage.width / 2, -loadedImage.height / 2);
    
    const blob = await new Promise((resolve) => {
      canvas.toBlob(resolve, 'image/png');
    });
    
    const rotatedUrl = URL.createObjectURL(blob);
    
    setFormData(prev => ({
      ...prev,
      uploadedImages: prev.uploadedImages.map((img, i) => 
        i === index ? rotatedUrl : img
      )
    }));
    
    if (processedImages.length > 0) {
      setProcessedImages(prev => prev.map((img, i) => 
        i === index ? rotatedUrl : img
      ));
    }
    
    URL.revokeObjectURL(imageUrl);
    
    // ❌ COMMENT OUT - No success message
    // showSuccess(`Image rotated ${degrees > 0 ? 'right' : 'left'} successfully!`);
    
  } catch (err) {
    console.error("Rotation failed:", err);
    showError("Failed to rotate image"); // ✅ Error message rakh sakte ho
  }
};

  const [loadingAspects, setLoadingAspects] = useState(false);

  const handleCategoryAspects = useCallback(async (categoryTerm) => {
    try {
      setLoadingAspects(true);
      const companyId = localStorage.getItem("company_id");
      const catResponse = await fetch(
        `https://crosslisting-backend-aacranbzdyd3ffbd.centralindia-01.azurewebsites.net/api/v1/getCategoriesFBThriftops/${companyId}?category_term=${categoryTerm}`,
        { method: "POST", headers: { "Content-Type": "application/json" } },
      );
      const catData = await catResponse.json();
      if (!catData.suggestedCategories?.length) {
        setLoadingAspects(false);
        return;
      }
      const firstCategory = catData.suggestedCategories[0].category;
      const aspectsResponse = await fetch(
        `https://crosslisting-backend-aacranbzdyd3ffbd.centralindia-01.azurewebsites.net/api/v1/getCategoryAspectsRAPIDAPI/${companyId}?category_id=${firstCategory.categoryId}`,
        { method: "POST", headers: { "Content-Type": "application/json" } },
      );
      const aspectsData = await aspectsResponse.json();
      setFormData((prev) => {
        const mappedAspects =
          aspectsData.category_aspects?.aspects
            .filter(
              (aspect) =>
                (aspect.aspectConstraint?.aspectUsage || "").toUpperCase() ===
                "RECOMMENDED",
            )
            .map((aspect) => {
              const key = aspect.localizedAspectName
                .replace(/\s+/g, "_")
                .toLowerCase();
              return {
                ...aspect,
                key,
                value: prev[key] || "",
              };
            }) || [];
        const newValues = {};
        mappedAspects.forEach((a) => {
          if (a.value) newValues[a.key] = a.value;
        });
        const allAspects =
          aspectsData.category_aspects?.aspects.map((aspect) => {
            const key = aspect.localizedAspectName
              .replace(/\s+/g, "_")
              .toLowerCase();
            return {
              ...aspect,
              key,
              value: prev[key] || "",
            };
          }) || [];
        return {
          ...prev,
          ...newValues,
          categoryAspects: mappedAspects,
          categoryId: firstCategory.categoryId,
          allCategoryAspects: allAspects,
        };
      });
      setLoadingAspects(false);
    } catch (err) {
      console.error("Error fetching aspects:", err);
      setLoadingAspects(false);
    }
  }, []);

  const [mappedSent, setMappedSent] = useState(false);

  useEffect(() => {
    if (
      !mappedSent &&
      autoFillResponse &&
      Array.isArray(formData.categoryAspects) &&
      formData.categoryAspects.length > 0
    ) {
      sendMappedCategories({
        autofillResponse: autoFillResponse,
        mappedAspects: formData.categoryAspects,
      });
      setMappedSent(true);
    }
  }, [autoFillResponse, formData.categoryAspects, mappedSent]);

  const sendMappedCategories = async ({ autofillResponse, mappedAspects }) => {
    try {
      const res = await fetch(
        "https://crosslisting-backend-aacranbzdyd3ffbd.centralindia-01.azurewebsites.net/api/v1/getMappedCategories",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            autofillResponse,
            mappedAspects,
          }),
        },
      );
      const data = await res.json();
      console.log("Backend combined response:", data);
      console.log("Filled aspects from Gemini:", data.geminiFilledAspects);
      const filled = data.geminiFilledAspects || [];
      setFormData((prev) => {
        const updatedAspects = prev.categoryAspects.map((aspect) => {
          const filledAspect = filled.find((f) => f.key === aspect.key);
          return {
            ...aspect,
            value: filledAspect?.value || "N/A",
          };
        });
        return {
          ...prev,
          categoryAspects: updatedAspects,
        };
      });
    } catch (err) {
      console.error("Failed to send combined data:", err);
    }
  };

  useEffect(() => {
    if (typeof formData.category === "string" && formData.category.trim()) {
      handleCategoryAspects(formData.category);
    }
  }, [formData.category, handleCategoryAspects]);

  // const handleAspectChange = (key, val) => {
  //   setFormData((prev) => ({
  //     ...prev,
  //     categoryAspects: prev.categoryAspects.map((a) =>
  //       a.key === key ? { ...a, value: val } : a,
  //     ),
  //     [key]: val,
  //   }));
  // };

  const handleAspectChange = (key, val) => {
    setFormData((prev) => {
      const updatedAllAspects = prev.allCategoryAspects.map((a) =>
        a.key === key ? { ...a, value: val } : a,
      );

      const updatedCategoryAspects = prev.categoryAspects.map((a) =>
        a.key === key ? { ...a, value: val } : a,
      );

      return {
        ...prev,
        allCategoryAspects: updatedAllAspects,
        categoryAspects: updatedCategoryAspects,
        [key]: val, // agar individual field bhi formData me chahiye
      };
    });
  };

  const handleAutoFill = async () => {
    if (!formData.uploadedImages.length) {
      showWarning("Please upload at least one image first!");
      return;
    }
    try {
      setIsAutoFilling(true);
      setAutoFillMessageIndex(0);
      const form = new FormData();
      for (let i = 0; i < formData.uploadedImages.length; i++) {
        const res = await fetch(formData.uploadedImages[i]);
        const blob = await res.blob();
        form.append("images", blob, `product-${i + 1}.png`);
      }
      await sendAutoFillRequest(form);
    } catch (err) {
      console.error("AutoFill Error", err);
      showError("Auto-fill failed");
    } finally {
      setIsAutoFilling(false);
    }
  };

  const sendAutoFillRequest = async (form) => {
    try {
      // Just extract keys from activeMetafields
      if (activeMetafields && activeMetafields.length > 0) {
        console.log("Sending full metafields objects:", activeMetafields);
        form.append("metafields", JSON.stringify(activeMetafields));
      }
      // Now send the request
      const response = await axios.post(
        "https://crosslisting-backend-aacranbzdyd3ffbd.centralindia-01.azurewebsites.net/api/autofill/analyze",
        form,
        { headers: { "Content-Type": "multipart/form-data" } },
      );

      console.log("Analyze response:", response.data);
      setAutoFillResponse(response.data);

      // Map metafields
      const data = response.data;

      // 🔥 yeh important line hai
      const mappedMetafields = buildMetafieldsFromAnalysis(
        activeMetafields,
        data,
      );

      console.log("mappedMetafields", mappedMetafields);
      // You can still update formData as before
      const analysis = response.data.analysis || {};
      const product_metadata = response.data.product_metadata || {};
      const price = response.data.price || {};
      const authenticity = response.data.authenticity || {};

      const conditionScore = Number(analysis.condition || 0);
      let condition = "POOR";
      if (conditionScore >= 10) condition = "NEW";
      else if (conditionScore >= 9) condition = "LIKE-NEW";
      else if (conditionScore >= 8) condition = "USED_EXCELLENT";
      else if (conditionScore >= 7) condition = "USED_GOOD";
      else if (conditionScore >= 6) condition = "USED_FAIR";

      const hierarchy = analysis.category || product_metadata.category || {};
      const categoryString = [
        hierarchy.l1_gender,
        hierarchy.l2_type,
        hierarchy.l3_category,
        hierarchy.l4_subcategory,
      ]
        .filter(Boolean)
        .join(" > ");

      setFormData((prev) => ({
        ...prev,
        metafields: mappedMetafields,
        title: analysis.seo_title || product_metadata.title || prev.title,
        description:
          analysis.seo_description ||
          product_metadata.description ||
          prev.description,
        sku: product_metadata.sku || prev.sku,
        brand: analysis.brand || "N/A",
        category: categoryString || prev.category,
        material: analysis.material || prev.material,
        color: analysis.color || prev.color,
        price: price.recommended || prev.price,
        pricerange:
          price.low && price.high
            ? `${price.low} - ${price.high}`
            : prev.pricerange,
        condition,
        condition_summary:
          analysis.condition_summary ||
          analysis.condition?.summary ||
          prev.condition_summary,
        authenticity_score: authenticity.score || prev.authenticity_score,
        authenticity_summary: authenticity.summary || prev.authenticity_summary,
        tags: analysis.seo_tags
          ? analysis.seo_tags.split(",").map((t) => t.trim())
          : product_metadata.seo_tags
            ? product_metadata.seo_tags.split(",").map((t) => t.trim())
            : prev.tags,
      }));
    } catch (err) {
      console.error("API Error:", err);
      showError("Auto-fill failed");
    }
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      setSubmissionSuccess(false);
      setSubmissionStep("📦 Preparing product data...");

      const selectedMps = formData.selectedMarketplaces || [];
      if (selectedMps.length === 0) {
        showWarning("⚠️ Please select at least one marketplace!");
        setIsSubmitting(false);
        return;
      }

      const companyId = localStorage.getItem("company_id");
      const results = [];
      const errors = [];
      const pendingActions = []; // For marketplaces that need user action

      // Create an array of marketplace promises
      const marketplacePromises = [];

      // ========== SHOPIFY ==========
      if (selectedMps.includes("shopify")) {
        const shopifyPromise = (async () => {
          try {
            setSubmissionStep("🛒 Processing Shopify...");

            const urlToBase64 = async (url) => {
              const res = await fetch(url);
              const blob = await res.blob();
              return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result.split(",")[1]);
                reader.onerror = reject;
                reader.readAsDataURL(blob);
              });
            };

            const imagesToSubmit = getImagesToSubmit();
            const images = [];

            for (let i = 0; i < imagesToSubmit.length; i++) {
              const imageUrl = imagesToSubmit[i];

              if (imageUrl.startsWith("blob:")) {
                console.log(
                  `📸 Image ${i + 1} is blob URL, converting to base64...`,
                );
                const base64 = await urlToBase64(imageUrl);
                images.push({
                  attachment: base64,
                  alt: `Product Image ${i + 1}`,
                });
              } else if (imageUrl.startsWith("http")) {
                console.log(
                  `📸 Image ${i + 1} is hosted URL: ${imageUrl.substring(0, 50)}...`,
                );
                images.push({
                  src: imageUrl,
                  alt: `Product Image ${i + 1}`,
                });
              } else {
                const base64 = await urlToBase64(imageUrl);
                images.push({
                  attachment: base64,
                  alt: `Product Image ${i + 1}`,
                });
              }
            }

            const metafieldsArray = [];
            if (
              formData.metafields &&
              Object.keys(formData.metafields).length > 0
            ) {
              Object.entries(formData.metafields).forEach(([key, value]) => {
                if (value?.trim()) {
                  const metafieldDef = activeMetafields.find(
                    (mf) => mf.key === key,
                  );
                  metafieldsArray.push({
                    namespace: metafieldDef?.namespace || "custom",
                    key: key,
                    value: value,
                    type: metafieldDef?.type || "single_line_text_field",
                  });
                }
              });
            }

            const payload = {
              product: {
                title: formData.title || "Test Product",
                body_html: formData.description || "<p>Description</p>",
                vendor: formData.brand || "Brand",
                product_type: formData.productType || "Garments",
                variants: [
                  {
                    option1: "Default",
                    price: String(formData.price || "1.00"),
                    sku: formData.sku || `SKU-${Date.now()}`,
                    inventory_quantity: parseInt(formData.quantity || 1),
                    inventory_management: "shopify",
                  },
                ],
                images,
                metafields: metafieldsArray,
              },
            };

            console.log("Shopify Payload:", payload);

            const res = await fetch(
              `https://crosslisting-backend-aacranbzdyd3ffbd.centralindia-01.azurewebsites.net/api/shopify/create/${companyId}`,
              {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
              },
            );

            const data = await res.json();
            console.log("Shopify Response:", data);

            const isSuccess =
              res.ok || (data && data.data && data.data.product);

            if (isSuccess) {
              return {
                marketplace: "shopify",
                success: true,
                data: data,
                warning: !res.ok
                  ? "Product created but API returned error status"
                  : undefined,
              };
            } else {
              throw new Error(
                data.message || data.error || "Shopify upload failed",
              );
            }
          } catch (err) {
            console.error("Shopify Error:", err);
            return {
              marketplace: "shopify",
              success: false,
              error: err.message,
            };
          }
        })();

        marketplacePromises.push(shopifyPromise);
      }

      // ========== EBAY ==========
      if (selectedMps.includes("ebay")) {
        const ebayPromise = (async () => {
          try {
            setSubmissionStep("🏷️ Processing eBay...");

            // Convert blob URLs to hosted URLs for eBay
            const urlToHostedUrl = async (url) => {
              if (
                typeof url === "string" &&
                url.startsWith("http") &&
                !url.startsWith("blob:")
              ) {
                return url;
              }

              try {
                const res = await fetch(url);
                const blob = await res.blob();

                const base64 = await new Promise((resolve, reject) => {
                  const reader = new FileReader();
                  reader.onloadend = () => {
                    const base64String = reader.result.split(",")[1];
                    resolve(base64String);
                  };
                  reader.onerror = reject;
                  reader.readAsDataURL(blob);
                });

                const uploadRes = await fetch(
                  "https://crosslisting-backend-aacranbzdyd3ffbd.centralindia-01.azurewebsites.net/api/image/upload",
                  {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                      image: base64,
                      filename: `ebay-image-${Date.now()}.png`,
                    }),
                  },
                );

                if (uploadRes.ok) {
                  const uploadData = await uploadRes.json();
                  return (
                    uploadData.url ||
                    uploadData.imageUrl ||
                    uploadData.image_url
                  );
                } else {
                  console.warn(
                    "Image upload endpoint not available, using blob URL",
                  );
                  return null;
                }
              } catch (err) {
                console.error("Error converting image URL:", err);
                return null;
              }
            };

            const imagesToProcess =
              processedImages.length > 0
                ? processedImages
                : formData.uploadedImages;
            const imageUrls = [];

            for (let i = 0; i < Math.min(imagesToProcess.length, 10); i++) {
              const url = imagesToProcess[i];
              if (
                typeof url === "string" &&
                url.startsWith("http") &&
                !url.startsWith("blob:")
              ) {
                imageUrls.push(url);
              } else {
                const hostedUrl = await urlToHostedUrl(url);
                if (hostedUrl) {
                  imageUrls.push(hostedUrl);
                }
              }
            }

            const finalImageUrls =
              imageUrls.length > 0
                ? imageUrls
                : ["https://via.placeholder.com/500"];

            console.log("eBay image URLs:", finalImageUrls);
            const isAuction = formData.listingType === "AUCTION";
            // const ebayPayload = {
            //   SKU: formData.sku || `SKU-${Date.now()}`,
            //   days: 0,
            //   marketplace_id: "EBAY_US",
            //   email: "test@example.com",
            //   categoryId: formData.categoryId || "177837",
            //   InventoryItem: {
            //     availability: {
            //       shipToLocationAvailability: {
            //         quantity: Number(formData.quantity) || 1,
            //       },
            //     },
            //     condition: (formData.condition || "NEW").toUpperCase(),
            //     packageWeightAndSize: {
            //       dimensions: {
            //         length: "10",
            //         width: "8",
            //         height: "2",
            //         unit: "INCH",
            //       },
            //       weight: {
            //         value: "1",
            //         unit: "POUND",
            //       },
            //     },
            //     product: {
            //       title: formData.title || "Product",
            //       description: formData.description || "No description",
            //       imageUrls: finalImageUrls || [],
            //       aspects: buildEbayAspects(formData.allCategoryAspects || []),
            //     },
            //   },
            //   // offerDetails: {
            //   //   pricingSummary: {
            //   //     price: {
            //   //       value: String(formData.price || "1.00"),
            //   //       currency: "USD",
            //   //     },
            //   //   },
            //   //   merchantLocationKey:
            //   //     savedEbayPolicies?.data?.location || "Warehouse1",
            //   //   listingPolicies: {
            //   //     fulfillmentPolicyId:
            //   //       savedEbayPolicies?.data?.shipping_policy_id || "",
            //   //     paymentPolicyId:
            //   //       savedEbayPolicies?.data?.payment_policy_id || "",
            //   //     returnPolicyId:
            //   //       savedEbayPolicies?.data?.return_policy_id || "",
            //   //   },
            //   // },
            //   offerDetails: {
            //     pricingSummary: isAuction
            //       ? {
            //           auctionStartPrice: {
            //             value: String(formData.price || "1.00"),
            //             currency: "USD",
            //           },
            //         }
            //       : {
            //           price: {
            //             value: String(formData.price || "1.00"),
            //             currency: "USD",
            //           },
            //         },

            //     merchantLocationKey:
            //       savedEbayPolicies?.data?.location || "Warehouse1",

            //     listingPolicies: {
            //       fulfillmentPolicyId:
            //         savedEbayPolicies?.data?.shipping_policy_id || "",
            //       paymentPolicyId:
            //         savedEbayPolicies?.data?.payment_policy_id || "",
            //       returnPolicyId:
            //         savedEbayPolicies?.data?.return_policy_id || "",
            //     },
            //   },
            // };
            const ebayPayload = {
              SKU: formData.sku || `SKU-${Date.now()}`,
              days: 0,
              marketplace_id: "EBAY_GB",
              email: "test@example.com",
              categoryId: formData.categoryId || "177837",
              InventoryItem: {
                availability: {
                  shipToLocationAvailability: {
                    quantity: Number(formData.quantity) || 1,
                  },
                },

                condition: (formData.condition || "NEW").toUpperCase(),
                packageWeightAndSize: {
                  dimensions: {
                    length: "10",
                    width: "8",
                    height: "2",
                    unit: "INCH",
                  },
                  weight: {
                    value: "1",
                    unit: "POUND",
                  },
                },
                product: {
                  title: formData.title || "Product",
                  description: formData.description || "No description",
                  imageUrls: finalImageUrls || [],
                  aspects: buildEbayAspects(formData.allCategoryAspects || []),
                },
              },
              offerDetails: {
                pricingSummary: isAuction
                  ? {
                      auctionStartPrice: {
                        value: String(formData.price || "1.00"),
                        currency: "GBP",
                      },
                    }
                  : {
                      price: {
                        value: String(formData.price || "1.00"),
                        currency: "GBP",
                      },
                    },

                merchantLocationKey:
                  savedEbayPolicies?.data?.location || "Warehouse1",

                listingPolicies: {
                  fulfillmentPolicyId:
                    savedEbayPolicies?.data?.shipping_policy_id || "",
                  paymentPolicyId:
                    savedEbayPolicies?.data?.payment_policy_id || "",
                  returnPolicyId:
                    savedEbayPolicies?.data?.return_policy_id || "",
                },
              },
            };
            console.log(ebayPayload);

            // const ebayRes = await fetch(
            //   `https://crosslisting-backend-aacranbzdyd3ffbd.centralindia-01.azurewebsites.net/api/v1/uploadProductToEbayThriftops/${companyId}`,
            //   {
            //     method: "PUT",
            //     headers: { "Content-Type": "application/json" },
            //     body: JSON.stringify(ebayPayload),
            //   },
            // );

            const endpoint =
              formData.listingType === "AUCTION"
                ? `https://crosslisting-backend-aacranbzdyd3ffbd.centralindia-01.azurewebsites.net/api/v1/uploadProductToEbayThriftopsAuction/${companyId}`
                : `https://crosslisting-backend-aacranbzdyd3ffbd.centralindia-01.azurewebsites.net/api/v1/uploadProductToEbayThriftops/${companyId}`;

            const ebayRes = await fetch(endpoint, {
              method: "PUT",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify(ebayPayload),
            });

            const ebayData = await ebayRes.json();

            if (ebayRes.ok) {
              return {
                marketplace: "ebay",
                success: true,
                data: ebayData,
              };
            } else {
              throw new Error(ebayData.message || "eBay upload failed");
            }
          } catch (err) {
            console.error("eBay Error:", err);
            return {
              marketplace: "ebay",
              success: false,
              error: err.message,
            };
          }
        })();

        marketplacePromises.push(ebayPromise);
      }

      // ========== POSHMARK ==========
      if (selectedMps.includes("poshmark")) {
        const poshmarkPromise = (async () => {
          try {
            setSubmissionStep("👗 Processing Poshmark...");
            const poshmarkResult = await uploadToPoshmark();

            // Check if user action is required
            if (!poshmarkResult.success && poshmarkResult.requiresUserAction) {
              return {
                marketplace: "poshmark",
                success: false,
                error: poshmarkResult.error,
                requiresUserAction: true,
              };
            }

            return {
              marketplace: "poshmark",
              success: poshmarkResult.success,
              data: poshmarkResult.data,
              error: poshmarkResult.error,
            };
          } catch (err) {
            return {
              marketplace: "poshmark",
              success: false,
              error: err.message,
            };
          }
        })();

        marketplacePromises.push(poshmarkPromise);
      }

      // ========== VINTED ==========
      // ========== VINTED ==========
      if (selectedMps.includes("vinted")) {
        const vintedPromise = (async () => {
          try {
            setSubmissionStep("👕 Processing Vinted...");
            const vintedResult = await uploadToVinted();

            // Agar product ID mil gaya to database mein save ho chuka hai
            if (vintedResult.success) {
              console.log(`✅ Vinted Product ID: ${vintedResult.productId}`);

              // Yahan aap product ID use kar sakte hain
              // Jaise state mein store karna, etc.

              return {
                marketplace: "vinted",
                success: true,
                data: vintedResult.data,
                productId: vintedResult.productId,
                productUrl: vintedResult.productUrl,
              };
            }

            // Check if user action is required (extension not connected)
            if (!vintedResult.success && vintedResult.requiresUserAction) {
              return {
                marketplace: "vinted",
                success: false,
                error: vintedResult.error,
                requiresUserAction: true,
                message: "Extension not connected - User action required",
              };
            }

            // Timeout case
            if (!vintedResult.success && vintedResult.timeout) {
              return {
                marketplace: "vinted",
                success: false,
                error: vintedResult.error,
                timeout: true,
                message: "Upload may have completed but ID not received",
              };
            }

            return {
              marketplace: "vinted",
              success: false,
              error: vintedResult.error,
            };
          } catch (err) {
            console.error("❌ Vinted Promise Error:", err);
            return {
              marketplace: "vinted",
              success: false,
              error: err.message,
            };
          }
        })();

        marketplacePromises.push(vintedPromise);
      }

      // Execute all marketplace uploads in parallel with a single progress message
      setSubmissionStep(
        `📤 Uploading to ${marketplacePromises.length} marketplace(s)...`,
      );

      // Use Promise.allSettled to handle both success and failures without stopping
      const settledResults = await Promise.allSettled(marketplacePromises);

      // Track results for final summary
      const successful = [];
      const failed = [];
      const pending = []; // For marketplaces that need user action

      // Process results
      settledResults.forEach((result) => {
        if (result.status === "fulfilled") {
          results.push(result.value);

          if (result.value.success) {
            successful.push(result.value.marketplace);

            // Show appropriate success message
            if (result.value.marketplace === "poshmark") {
              showSuccess(
                "✅ Poshmark: Automation started! Check your browser tab.",
              );
            } else if (result.value.marketplace === "vinted") {
              showSuccess(
                "✅ Vinted: Automation Started!\n\n" +
                  "📋 What's happening:\n" +
                  "• Vinted tab will activate\n" +
                  "• Form will be filled automatically\n" +
                  "• You'll see progress notifications\n" +
                  "• Item will be uploaded in 30-60 seconds\n\n" +
                  "👀 Watch the Vinted tab!",
              );
            } else if (result.value.marketplace === "shopify") {
              showSuccess("✅ Shopify: Product created successfully!");
            } else if (result.value.marketplace === "ebay") {
              showSuccess("✅ eBay: Listing created successfully!");
            }
          } else {
            // Check if this is a user-action-required case
            if (result.value.requiresUserAction) {
              pending.push(result.value.marketplace);
              // Store the error message for summary
              pendingActions.push({
                marketplace: result.value.marketplace,
                error: result.value.error,
              });
            } else {
              failed.push(`${result.value.marketplace}: ${result.value.error}`);
              showError(
                `❌ ${result.value.marketplace} failed: ${result.value.error}`,
              );
            }
          }
        } else {
          // Handle promise rejection
          failed.push(
            `Unknown marketplace: ${result.reason?.message || "Unknown error"}`,
          );
          showError(`❌ A marketplace upload failed unexpectedly`);
        }
      });

      // ========== CHECK RESULTS ==========
      const successfulMarketplaces = results.filter((r) => r && r.success);

      // If no marketplaces succeeded and no pending actions, throw error
      if (successfulMarketplaces.length === 0 && pending.length === 0) {
        throw new Error("All selected marketplace uploads failed");
      }

      // ========== CREATE PRODUCT IN DATABASE (only if at least one succeeded) ==========
      let productId = null;

      if (successfulMarketplaces.length > 0) {
        setSubmissionStep("💾 Creating product in database...");

        // Ensure we send hosted URLs (no blob:) to the DB
        const imagesForDatabase = await uploadImagesToERP(getImagesToSubmit());

        const createProductRes = await fetch(
          "https://crosslisting-backend-aacranbzdyd3ffbd.centralindia-01.azurewebsites.net/api/create-product",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              company_id: companyId,
              title: formData.title,
              sku: formData.sku || `SKU-${Date.now()}`,
              description: formData.description,
              seo_tags: formData.seo_tags || "",
              price: Number(formData.price || 1),
              quantity: Number(formData.quantity || 1),
              images: imagesForDatabase || [],
            }),
          },
        );

        const createProductData = await createProductRes.json();
        if (!createProductRes.ok) {
          throw new Error(createProductData.error || "Product creation failed");
        }

        productId = createProductData.product_id;

        // ========== INSERT MARKETPLACE RECORDS ==========
        setSubmissionStep("📝 Saving marketplace records...");

        // Create promises for marketplace record insertion
        const recordPromises = [];

        for (const mp of successfulMarketplaces) {
          if (mp.marketplace === "ebay") {
            recordPromises.push(
              fetch("/api/ebay-product-insert", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  product_id: productId,
                  ebay_item_id:
                    mp.data.publishOffer?.listingId ||
                    mp.data.draftOffer?.offerId,
                  sku: formData.sku,
                  title: formData.title,
                  description: formData.description,
                  quantity: Number(formData.quantity),
                  price: Number(formData.price),
                  currency: "USD",
                  category_id: formData.categoryId,
                  ebay_store_id: companyId,
                }),
              }),
            );
          }

          if (mp.marketplace === "shopify") {
            console.log("Shopify response:", mp.data?.data);
            const product = mp.data?.data;
            const variant = product?.variants?.[0];

            const metafieldsArray = [];
            if (
              formData.metafields &&
              Object.keys(formData.metafields).length > 0
            ) {
              Object.entries(formData.metafields).forEach(([key, value]) => {
                if (value?.trim()) {
                  const metafieldDef = activeMetafields.find(
                    (mf) => mf.key === key,
                  );
                  metafieldsArray.push({
                    namespace: metafieldDef?.namespace || "custom",
                    key: key,
                    value: value,
                    type: metafieldDef?.type || "single_line_text_field",
                  });
                }
              });
            }

            recordPromises.push(
              fetch(
                "https://crosslisting-backend-aacranbzdyd3ffbd.centralindia-01.azurewebsites.net/api/shopify-product-insert",
                {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({
                    product_id: productId,
                    shopify_product_id: product?.id,
                    shopify_variant_id: variant?.id,
                    inventory_item_id: variant?.inventory_item_id,
                    sku: variant?.sku,
                    barcode: variant?.barcode || null,
                    product_type: product?.product_type,
                    vendor: product?.vendor,
                    price: variant?.price,
                    compare_at_price: variant?.compare_at_price,
                    quantity: variant?.inventory_quantity,
                    tags: product?.tags || [],
                    variants: product?.variants || [],
                    metafields: metafieldsArray,
                    shopify_store_id: companyId,
                  }),
                },
              ),
            );
          }
        }

        // Execute all record insertions in parallel
        if (recordPromises.length > 0) {
          await Promise.allSettled(recordPromises);
        }
      }

      // ========== FINAL SUMMARY ==========
      setSubmissionStep("✅ Finalizing...");

      // Build summary message
      let summaryMessage = "";

      if (successful.length > 0) {
        summaryMessage += `✅ Successful: ${successful.length}\n`;
        summaryMessage +=
          successful
            .map((m) => `   • ${m.charAt(0).toUpperCase() + m.slice(1)}`)
            .join("\n") + "\n\n";
      }

      if (pending.length > 0) {
        summaryMessage += `⚠️ Need Action: ${pending.length}\n`;
        summaryMessage +=
          pending
            .map((m) => `   • ${m.charAt(0).toUpperCase() + m.slice(1)}`)
            .join("\n") + "\n\n";

        // Add specific instructions for Vinted
        if (pending.includes("vinted")) {
          summaryMessage += "📋 Vinted Instructions:\n";
          summaryMessage += "   1. Open Vinted website in Chrome\n";
          summaryMessage += "   2. Go to create listing page\n";
          summaryMessage += "   3. Refresh the page\n";
          summaryMessage +=
            "   4. Wait for 'Ready for Automation' notification\n";
          summaryMessage += "   5. Click 'Create Listing' again\n\n";
        }

        // Add specific instructions for Poshmark
        if (pending.includes("poshmark")) {
          summaryMessage += "📋 Poshmark Instructions:\n";
          summaryMessage += "   1. Open Poshmark website\n";
          summaryMessage += "   2. Go to create listing page\n";
          summaryMessage += "   3. Refresh the page\n";
          summaryMessage += "   4. Click 'Create Listing' again\n\n";
        }
      }

      if (failed.length > 0) {
        summaryMessage += `❌ Failed: ${failed.length}\n`;
        summaryMessage += failed.map((f) => `   • ${f}`).join("\n");
      }

      setSubmissionSuccess(true);

      setTimeout(() => {
        if (failed.length === 0 && pending.length === 0) {
          // All successful
          showSuccess(
            `✅ All Done!\n\n` +
              `${successful.length} marketplace(s) processed successfully.\n\n` +
              `Check your browser tabs for automation in progress.`,
          );

          // Reload after 2 seconds
          setTimeout(() => {
            localStorage.removeItem("appState_addProduct");
            window.location.reload();
          }, 2000);
        } else if (successful.length > 0 && pending.length > 0) {
          // Some succeeded, some need action
          showWarning(summaryMessage);
          setIsSubmitting(false);
        } else if (successful.length === 0 && pending.length > 0) {
          // All need action
          showWarning(summaryMessage);
          setIsSubmitting(false);
        } else {
          // Some failed
          showWarning(summaryMessage);
          setIsSubmitting(false);
        }
      }, 1000);

      // Try to focus Vinted tab if it was successful
      // Try to focus Vinted tab if it was successful
      if (successful.includes("vinted")) {
        // Find the successful Vinted result
        const vintedResult = results.find(
          (r) => r.marketplace === "vinted" && r.success,
        );

        if (vintedResult && vintedResult.productId) {
          console.log(`🎯 Vinted Product ID: ${vintedResult.productId}`);

          // Yahan aap product ID ke saath kuch bhi kar sakte hain
          // Jaise analytics bhejna, notification dena, etc.

          showSuccess(
            `✅ Vinted Success!\n\n` +
              `Product ID: ${vintedResult.productId}\n` +
              `Saved to database automatically.`,
          );
        }

        try {
          if (typeof chrome !== "undefined" && chrome.tabs) {
            chrome.tabs.query({ url: "*://*.vinted.com/*" }, (tabs) => {
              if (tabs && tabs.length > 0) {
                chrome.tabs.update(tabs[0].id, { active: true });
              }
            });
          }
        } catch (e) {
          console.log("Could not focus Vinted tab:", e);
        }
      }

      // Try to focus Poshmark tab if it was successful
      if (successful.includes("poshmark")) {
        try {
          if (typeof chrome !== "undefined" && chrome.tabs) {
            chrome.tabs.query({ url: "*://*.poshmark.com/*" }, (tabs) => {
              if (tabs && tabs.length > 0) {
                chrome.tabs.update(tabs[0].id, { active: true });
              }
            });
          }
        } catch (e) {
          console.log("Could not focus Poshmark tab:", e);
        }
      }
    } catch (err) {
      console.error("❌ Global Error:", err);
      showError(`❌ Error: ${err.message || "Something went wrong"}`);
      setIsSubmitting(false);
    }
  };

  const handleExportCSV = () => {
    if (!formData) return;
    const headers = [
      "Marketplace",
      "Title",
      "Description",
      "SKU",
      "Price",
      "Quantity",
      "Brand",
      "Category",
      "Condition",
      "Color",
      "Variants",
    ];
    const rows = [];
    rows.push([
      "All Marketplaces",
      formData.title,
      formData.description,
      formData.sku,
      formData.price,
      formData.quantity,
      formData.brand,
      formData.category,
      formData.condition,
      formData.color,
      formData.variants
        .map((v) => `Size: ${v.size}, Color: ${v.color}, SKU: ${v.sku}`)
        .join(" | "),
    ]);
    formData.selectedMarketplaces.forEach((id) => {
      let marketplaceName = marketplaces.find((m) => m.id === id)?.name || id;
      let row = [
        marketplaceName,
        formData.title,
        formData.description,
        formData.sku,
        formData.price,
        formData.quantity,
        formData.brand,
        formData.category,
        formData.condition,
        formData.color,
        formData.variants
          .map((v) => `Size: ${v.size}, Color: ${v.color}, SKU: ${v.sku}`)
          .join(" | "),
      ];
      rows.push(row);
    });
    const csvContent = [
      headers.join(","),
      ...rows.map((r) => r.map((v) => `"${v}"`).join(",")),
    ].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "product_listing.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  const handleSwapImages = (first, second) => {
    const items = [...processedImages];

    [items[first], items[second]] = [items[second], items[first]];

    setProcessedImages(items);
  };
  return (
    <>
      {isAutoFilling && (
        <AutoFillLoader message={autoFillMessages[autoFillMessageIndex]} />
      )}
      {isSubmitting && (
        <SubmissionLoader step={submissionStep} isSuccess={submissionSuccess} />
      )}
      {/* <Joyride
        steps={stepstofollow}
        run={run}
        stepIndex={stepIndex}
        callback={handleJoyrideCallback}
        continuous={false}
        showSkipButton={false}
        hideCloseButton={true}
        disableCloseOnEsc={true}
        spotlightClicks={true}
        styles={{
          options: {
            overlayColor: "rgba(0,0,0,0.85)",
            zIndex: 10000,
          },
        }}
      /> */}
      <MDBContainer fluid className="add-product-wizard">
        <div className="wizard-container">
          <div className="wizard-header">
            <h1 className="wizard-title">Add Product</h1>
            <p className="wizard-subtitle">
              Create a new product listing in 5 simple steps
            </p>
          </div>

          <div className="stepper">
            {steps.map((step, index) => (
              <div key={step.id} className="step-container">
                <div
                  className={`step ${currentStep >= step.id ? "active" : ""} ${
                    currentStep === step.id ? "current" : ""
                  } ${isStepClickable(step.id) ? "clickable" : "disabled"}`}
                >
                  <div
                    className="step-number"
                    onClick={() => goToStep(step.id)}
                    style={{
                      cursor: isStepClickable(step.id) ? "pointer" : "default",
                    }}
                  >
                    {currentStep > step.id ? "✓" : step.id}
                  </div>
                  <div className="step-label">{step.title}</div>
                </div>
                {index < steps.length - 1 && (
                  <div
                    className={`step-connector ${
                      currentStep > step.id ? "completed" : ""
                    }`}
                  ></div>
                )}
              </div>
            ))}
          </div>

          <div className="step-content">
            {currentStep === 1 && (
              <ImageUploadStep
                formData={formData}
                blurTextIndex={blurTextIndex}
                stepIndex={stepIndex}
                moveNext={moveNext}
                handleFileUpload={handleFileUpload}
                handleDragOver={handleDragOver}
                handleDragDrop={handleDragDrop}
                removeImage={removeImage}
                rotateImage={rotateImage}        // Right rotate
                rotateImageLeft={rotateImageLeft} // Left rotate

              />
            )}

            {currentStep === 2 && (
              <ListingDetailsStep
                formData={formData}
                processingImages={processingImages}
                progress={progress}
                blurTextIndex={blurTextIndex}
                loadingAspects={loadingAspects}
                activeMetafields={activeMetafields}
                loadingMetafields={loadingMetafields}
                handleAutoFill={handleAutoFill}
                handleInputChange={handleInputChange}
                handleAspectChange={handleAspectChange}
                handleVariantChange={handleVariantChange}
                addVariant={addVariant}
                removeVariant={removeVariant}
              />
            )}

            {currentStep === 3 && (
              <MarketplacesStep
                formData={formData}
                processingImages={processingImages}
                progress={progress}
                ebayConnected={ebayConnected}
                checkingEbay={checkingEbay}
                poshmarkConnected={poshmarkConnected}
                checkingPoshmark={checkingPoshmark}
                poshmarkSubCategories={poshmarkSubCategories}
                vintedConnected={vintedConnected}
                checkingVinted={checkingVinted}
                vintedMainCategories={vintedMainCategories}
                vintedSubCategories={vintedSubCategories}
                vintedCategoryHistory={vintedCategoryHistory}
                vintedCurrentCategoryId={vintedCurrentCategoryId}
                vintedSelectedCategoryPath={vintedSelectedCategoryPath}
                vintedLoadingCategories={vintedLoadingCategories}
                handleMarketplaceToggle={handleMarketplaceToggle}
                handlePoshmarkIntegrate={handlePoshmarkIntegrate}
                handlePoshmarkCategoryChange={handlePoshmarkCategoryChange}
                handleVintedIntegrate={handleVintedIntegrate}
                handleVintedCategoryClick={handleVintedCategoryClick}
                handleVintedBack={handleVintedBack}
                resetVintedCategory={resetVintedCategory}
                handleInputChange={handleInputChange}
                handleVariantChange={handleVariantChange}
              />
            )}

            {currentStep === 4 && (
              <ImageProcessingStep
                formData={formData}
                processing={processing}
                processingImages={processingImages}
                progress={progress}
                processedImages={processedImages}
                handleSwapImages={handleSwapImages}
                handleInputChange={handleInputChange}
                handleStartImageEditing={handleStartImageEditing}
              />
            )}

            {currentStep === 5 && (
              <ReviewStep
                formData={formData}
                processedImages={processedImages}
                poshmarkConnected={poshmarkConnected}
                vintedConnected={vintedConnected}
                getImagesToSubmit={getImagesToSubmit}
              />
            )}
          </div>

          <div className="wizard-navigation">
            <MDBBtn
              color="light"
              disabled={currentStep === 1}
              onClick={prevStep}
              className="nav-btn"
            >
              Back
            </MDBBtn>
            {currentStep < steps.length ? (
              <MDBBtn
                className="btn-red"
                disabled={!isStepValid()}
                onClick={nextStep}
              >
                Next
              </MDBBtn>
            ) : (
              <>
                <MDBBtn
                  className="btn-red"
                  onClick={handleSubmit}
                  disabled={processingImages || isSubmitting}
                >
                  {isSubmitting ? "Creating..." : "Create Listing"}
                </MDBBtn>
                <MDBBtn
                  className="btn-outline-dark ms-3"
                  onClick={handleExportCSV}
                  disabled={processingImages}
                >
                  Export CSV
                </MDBBtn>
              </>
            )}
          </div>
        </div>
      </MDBContainer>
      <MessageBoxComponent />
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </>
  );
};

export default AddProduct;
